import React, { useEffect, useState, useRef } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import {
    Button,
    Input,
    Modal,
    Tooltip,
    Card,
    Space,
    message,
    Row,
    Col,
    Typography,
    Tag,
    Form,
    Input as AntInput,
    Alert,
} from "antd";
import {
    SearchOutlined,
    ReloadOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    UndoOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import { getRequest, putRequest } from "../../Utils/AppApi";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { getUTCToLocalDateTimeFormat } from "../../Utils/function";

const { Title, Text } = Typography;
const { TextArea } = AntInput;

const BeneficiaryManagement = () => {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [beneficiaries, setBeneficiaries] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(50);
    const [ifscFilter, setIfscFilter] = useState("");

    // Bulk Deactivation
    const [isDeactivateModalVisible, setDeactivateModalVisible] = useState(false);
    const [deactivateIfsc, setDeactivateIfsc] = useState("");
    const [deactivateRemark, setDeactivateRemark] = useState(
        "Invalid IFSC Code, Please update it"
    );

    // Bulk Reactivation
    const [isReactivateModalVisible, setReactivateModalVisible] = useState(false);
    const [reactivateIfsc, setReactivateIfsc] = useState("");
    const [reactivateRemark, setReactivateRemark] = useState("");

    // Individual toggle
    const [individualLoading, setIndividualLoading] = useState(false);

    // View modal
    const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
    const [isConfirmModalVisible, setConfirmModalVisible] = useState(false);

    // ─── Columns ───
    const columns = [
        {
            name: "Customer Mobile",
            selector: (row) => row.customerMobile || "-",
            width: "150px",
            sortable: true,
        },
        {
            name: "Customer Name",
            selector: (row) => row.customerName || "-",
            width: "180px",
            sortable: true,
        },
        {
            name: "Beneficiary Name",
            selector: (row) => row.beneName || "-",
            width: "180px",
            sortable: true,
        },
        {
            name: "API Name",
            selector: (row) => row.apiName || "-",
            width: "180px",
            sortable: true,
        },
        {
            name: "Account Number",
            selector: (row) => row.accountNumber || "-",
            width: "160px",
            sortable: true,
        },
        {
            name: "IFSC Code",
            selector: (row) => (
                <Tooltip title={row.ifscCode}>
                    <span style={{ fontFamily: "monospace" }}>{row.ifscCode}</span>
                </Tooltip>
            ),
            width: "130px",
            sortable: true,
        },
        {
            name: "Bank Name",
            selector: (row) => row.bankName || row.beneBankName || "-",
            width: "200px",
            sortable: true,
        },
        {
            name: "Status",
            selector: (row) =>
                row.isActive ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                        Active
                    </Tag>
                ) : (
                    <Tag color="red" icon={<CloseCircleOutlined />}>
                        Inactive
                    </Tag>
                ),
            width: "120px",
            center: true,
        },
        {
            name: "Verified",
            selector: (row) =>
                row.isVerified ? (
                    <Tag color="blue">Verified</Tag>
                ) : (
                    <Tag color="orange">Pending</Tag>
                ),
            width: "120px",
            center: true,
        },
        {
            name: "Remarks",
            selector: (row) => row.remarks || "-",
            width: "250px",
            wrap: true,
        },
        {
            name: "Created At",
            selector: (row) => getUTCToLocalDateTimeFormat(row.createdAt),
            width: "180px",
            sortable: true,
        },
        {
            name: "Actions",
            selector: (row) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => viewBeneficiary(row)}
                        size="small"
                    />
                    {row.isActive ? (
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeactivateSingle(row)}
                            size="small"
                            title="Deactivate"
                        />
                    ) : (
                        <Button
                            type="text"
                            style={{ color: "#52c41a" }}
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleActivateSingle(row)}
                            size="small"
                            title="Activate"
                        />
                    )}
                </Space>
            ),
            width: "140px",
            center: true,
            ignoreRowClick: true,
        },
    ];

    // ─── API Helpers ───
    const objectToQueryString = (obj) => {
        const str = [];
        for (const p in obj) {
            if (obj.hasOwnProperty(p) && obj[p] !== undefined && obj[p] !== null && obj[p] !== "") {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        }
        return str.join("&");
    };

    const fetchBeneficiaries = async () => {
        setLoading(true);
        const params = {
            page,
            limit: perPage,
        };
        if (ifscFilter.trim()) {
            params.ifscCode = ifscFilter.trim();
        }
        const query = objectToQueryString(params);
        try {
            const response = await getRequest(`/admin/beneficiaries?${query}`, history);
            if (response.success) {
                setBeneficiaries(response.data || []);
                setTotalRecords(response.totalDocs || 0);
            } else {
                message.error(response.message || "Failed to fetch beneficiaries.");
                setBeneficiaries([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching beneficiaries:", error);
            message.error("Failed to fetch beneficiaries.");
        } finally {
            setLoading(false);
        }
    };

    // Bulk deactivation
    const deactivateByIfsc = async (ifsc, remark) => {
        setLoading(true);
        try {
            const response = await putRequest(
                "/admin/beneficiaries/deactivate-by-ifsc",
                { ifscCode: ifsc, remarks: remark },
                history
            );
            if (response.success) {
                message.success(response.message);
                setDeactivateModalVisible(false);
                fetchBeneficiaries();
            } else {
                message.error(response.message || "Deactivation failed.");
            }
        } catch (error) {
            console.error("Error deactivating:", error);
            message.error("Deactivation failed.");
        } finally {
            setLoading(false);
        }
    };

    // Bulk reactivation
    const reactivateByIfsc = async (ifsc, remark) => {
        setLoading(true);
        try {
            const response = await putRequest(
                "/admin/beneficiaries/reactivate-by-ifsc",
                { ifscCode: ifsc, remarks: remark },
                history
            );
            if (response.success) {
                message.success(response.message);
                setReactivateModalVisible(false);
                fetchBeneficiaries();
            } else {
                message.error(response.message || "Reactivation failed.");
            }
        } catch (error) {
            console.error("Error reactivating:", error);
            message.error("Reactivation failed.");
        } finally {
            setLoading(false);
        }
    };

    // Individual toggle
    const toggleIndividualStatus = async (id, isActive, remarks = null) => {
        setIndividualLoading(true);
        try {
            const response = await putRequest(
                `/admin/beneficiaries/${id}/toggle-status`,
                { isActive, remarks },
                history
            );
            if (response.success) {
                message.success(`Beneficiary ${isActive ? 'activated' : 'deactivated'} successfully.`);
                fetchBeneficiaries();
            } else {
                message.error(response.message || "Action failed.");
            }
        } catch (error) {
            console.error("Error toggling status:", error);
            message.error("Action failed.");
        } finally {
            setIndividualLoading(false);
        }
    };

    // ─── Handlers ───
    const handleSearch = () => {
        setPage(1);
        fetchBeneficiaries();
    };

    const handleClear = () => {
        setIfscFilter("");
        setPage(1);
        setTimeout(() => fetchBeneficiaries(), 0);
    };

    const handlePageChange = (newPage) => setPage(newPage);
    const handlePerRowsChange = (newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(1);
    };

    const viewBeneficiary = (row) => {
        setSelectedBeneficiary(row);
        setConfirmModalVisible(true);
    };

    // Individual deactivate – confirm modal with remark
    const handleDeactivateSingle = (row) => {
        let remark = "Deactivated by admin";
        Modal.confirm({
            title: "Deactivate Beneficiary",
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Are you sure you want to deactivate this beneficiary?</p>
                    <p><strong>Beneficiary:</strong> {row.beneName}</p>
                    <p><strong>Account:</strong> {row.accountNumber}</p>
                    <p><strong>IFSC:</strong> {row.ifscCode}</p>
                    <div style={{ marginTop: 10 }}>
                        <label>Remarks (optional):</label>
                        <Input.TextArea
                            rows={2}
                            placeholder="Reason for deactivation"
                            defaultValue={remark}
                            id="deactivate-remark-input"
                        />
                    </div>
                </div>
            ),
            okText: "Deactivate",
            okType: "danger",
            onOk: () => {
                const input = document.getElementById('deactivate-remark-input');
                const finalRemark = input ? input.value : remark;
                toggleIndividualStatus(row._id, false, finalRemark);
            },
        });
    };

    // Individual activate – simple confirm (no remarks needed)
    const handleActivateSingle = (row) => {
        Modal.confirm({
            title: "Activate Beneficiary",
            icon: <CheckCircleOutlined />,
            content: (
                <div>
                    <p>Are you sure you want to activate this beneficiary?</p>
                    <p><strong>Beneficiary:</strong> {row.beneName}</p>
                    <p><strong>Account:</strong> {row.accountNumber}</p>
                    <p><strong>IFSC:</strong> {row.ifscCode}</p>
                </div>
            ),
            okText: "Activate",
            onOk: () => {
                toggleIndividualStatus(row._id, true, null);
            },
        });
    };

    // Bulk deactivation submit
    const handleDeactivateSubmit = () => {
        if (!deactivateIfsc.trim()) {
            message.error("Please enter an IFSC code.");
            return;
        }
        Modal.confirm({
            title: "Confirm Deactivation",
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>
                        Are you sure you want to deactivate <strong>all active beneficiaries</strong> with IFSC:{" "}
                        <Text code>{deactivateIfsc}</Text>?
                    </p>
                    <p>Remark: <Text italic>{deactivateRemark}</Text></p>
                    <p style={{ color: "red", fontWeight: "bold" }}>
                        This action cannot be undone.
                    </p>
                </div>
            ),
            okText: "Deactivate All",
            okType: "danger",
            onOk: () => {
                deactivateByIfsc(deactivateIfsc, deactivateRemark);
            },
        });
    };

    // Bulk reactivation submit
    const handleReactivateSubmit = () => {
        if (!reactivateIfsc.trim()) {
            message.error("Please enter an IFSC code.");
            return;
        }
        Modal.confirm({
            title: "Confirm Reactivation",
            icon: <SyncOutlined />,
            content: (
                <div>
                    <p>
                        Are you sure you want to reactivate <strong>all inactive beneficiaries</strong> with IFSC:{" "}
                        <Text code>{reactivateIfsc}</Text>?
                    </p>
                    {reactivateRemark && <p>Remark: <Text italic>{reactivateRemark}</Text></p>}
                </div>
            ),
            okText: "Reactivate All",
            onOk: () => {
                reactivateByIfsc(reactivateIfsc, reactivateRemark);
            },
        });
    };

    // ─── Fetch on page/perPage change ───
    useEffect(() => {
        fetchBeneficiaries();
    }, [page, perPage]);

    // ─── Render ───
    return (
        <div style={{ padding: "20px", background: "#f8f9fa", minHeight: "100vh" }}>
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
                {/* Header */}
                <div style={{ marginBottom: "24px" }}>
                    <Title level={2} style={{ margin: 0 }}>
                        Beneficiary Management
                    </Title>
                    <Text type="secondary">Search, view, and manage beneficiary status.</Text>
                </div>

                {/* Filters Card */}
                <Card style={{ marginBottom: "24px", borderRadius: "8px" }}>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Input
                                placeholder="Search by IFSC Code"
                                value={ifscFilter}
                                onChange={(e) => setIfscFilter(e.target.value)}
                                onPressEnter={handleSearch}
                                prefix={<SearchOutlined />}
                                allowClear
                            />
                        </Col>
                        <Col xs={24} sm={12} md={16} lg={18}>
                            <Space>
                                <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                                    Search
                                </Button>
                                <Button onClick={handleClear} icon={<ReloadOutlined />}>
                                    Clear
                                </Button>
                                <Button
                                    type="dashed"
                                    danger
                                    onClick={() => setDeactivateModalVisible(true)}
                                    icon={<DeleteOutlined />}
                                >
                                    Deactivate by IFSC
                                </Button>
                                <Button
                                    type="dashed"
                                    style={{ borderColor: "#52c41a", color: "#52c41a" }}
                                    onClick={() => setReactivateModalVisible(true)}
                                    icon={<UndoOutlined />}
                                >
                                    Reactivate by IFSC
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {/* Data Table */}
                <Card style={{ borderRadius: "8px", overflow: "hidden" }}>
                    <DataTable
                        columns={columns}
                        data={beneficiaries}
                        progressPending={loading}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRecords}
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        highlightOnHover
                        fixedHeader
                        fixedHeaderScrollHeight="calc(100vh - 400px)"
                        noDataComponent={
                            <div style={{ padding: "48px 0", textAlign: "center", color: "#718096" }}>
                                <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                                    No beneficiaries found
                                </div>
                                <p style={{ fontSize: "14px" }}>
                                    Try adjusting your search filters
                                </p>
                            </div>
                        }
                        customStyles={{
                            headCells: {
                                style: {
                                    backgroundColor: "#f8f9fa",
                                    padding: "12px 16px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#4a5568",
                                    borderBottom: "1px solid #e2e8f0",
                                },
                            },
                            cells: {
                                style: {
                                    padding: "12px 16px",
                                    fontSize: "12px",
                                    color: "#4a5568",
                                    borderBottom: "1px solid #f1f5f9",
                                },
                            },
                        }}
                    />
                </Card>

                {/* ─── Modal: Bulk Deactivate ─── */}
                <Modal
                    title="Deactivate Beneficiaries by IFSC"
                    open={isDeactivateModalVisible}
                    onCancel={() => setDeactivateModalVisible(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setDeactivateModalVisible(false)}>
                            Cancel
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            danger
                            onClick={handleDeactivateSubmit}
                            loading={loading}
                        >
                            Deactivate All
                        </Button>,
                    ]}
                    width={600}
                >
                    <Form layout="vertical">
                        <Form.Item
                            label="IFSC Code"
                            required
                            help="All active beneficiaries with this IFSC will be deactivated."
                        >
                            <Input
                                placeholder="e.g. SBIN0001234"
                                value={deactivateIfsc}
                                onChange={(e) => setDeactivateIfsc(e.target.value.toUpperCase())}
                                style={{ fontFamily: "monospace" }}
                            />
                        </Form.Item>
                        <Form.Item label="Remarks">
                            <TextArea
                                rows={3}
                                value={deactivateRemark}
                                onChange={(e) => setDeactivateRemark(e.target.value)}
                                placeholder="Reason for deactivation"
                            />
                        </Form.Item>
                        <Alert
                            message="Warning"
                            description="This will deactivate ALL active beneficiaries with the given IFSC code. This action is irreversible."
                            type="warning"
                            showIcon
                        />
                    </Form>
                </Modal>

                {/* ─── Modal: Bulk Reactivate ─── */}
                <Modal
                    title="Reactivate Beneficiaries by IFSC"
                    open={isReactivateModalVisible}
                    onCancel={() => setReactivateModalVisible(false)}
                    footer={[
                        <Button key="cancel" onClick={() => setReactivateModalVisible(false)}>
                            Cancel
                        </Button>,
                        <Button
                            key="submit"
                            type="primary"
                            style={{ background: "#52c41a", borderColor: "#52c41a" }}
                            onClick={handleReactivateSubmit}
                            loading={loading}
                        >
                            Reactivate All
                        </Button>,
                    ]}
                    width={600}
                >
                    <Form layout="vertical">
                        <Form.Item
                            label="IFSC Code"
                            required
                            help="All inactive beneficiaries with this IFSC will be reactivated."
                        >
                            <Input
                                placeholder="e.g. SBIN0001234"
                                value={reactivateIfsc}
                                onChange={(e) => setReactivateIfsc(e.target.value.toUpperCase())}
                                style={{ fontFamily: "monospace" }}
                            />
                        </Form.Item>
                        <Form.Item label="Remarks (optional)">
                            <TextArea
                                rows={3}
                                value={reactivateRemark}
                                onChange={(e) => setReactivateRemark(e.target.value)}
                                placeholder="Optional remark"
                            />
                        </Form.Item>
                        <Alert
                            message="Note"
                            description="This will reactivate all inactive beneficiaries with the given IFSC code."
                            type="info"
                            showIcon
                        />
                    </Form>
                </Modal>

                {/* ─── Modal: View Beneficiary Details ─── */}
                <Modal
                    title="Beneficiary Details"
                    open={isConfirmModalVisible}
                    onCancel={() => setConfirmModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setConfirmModalVisible(false)}>
                            Close
                        </Button>,
                    ]}
                    width={700}
                >
                    {selectedBeneficiary && (
                        <div>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Text strong>Customer Mobile</Text>
                                    <div>{selectedBeneficiary.customerMobile || "-"}</div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Customer Name</Text>
                                    <div>{selectedBeneficiary.customerName || "-"}</div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Beneficiary Name</Text>
                                    <div>{selectedBeneficiary.beneName || "-"}</div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Account Number</Text>
                                    <div>{selectedBeneficiary.accountNumber || "-"}</div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>IFSC Code</Text>
                                    <div style={{ fontFamily: "monospace" }}>
                                        {selectedBeneficiary.ifscCode || "-"}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Bank Name</Text>
                                    <div>{selectedBeneficiary.bankName || selectedBeneficiary.beneBankName || "-"}</div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Status</Text>
                                    <div>
                                        {selectedBeneficiary.isActive ? (
                                            <Tag color="green">Active</Tag>
                                        ) : (
                                            <Tag color="red">Inactive</Tag>
                                        )}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text strong>Verified</Text>
                                    <div>
                                        {selectedBeneficiary.isVerified ? (
                                            <Tag color="blue">Verified</Tag>
                                        ) : (
                                            <Tag color="orange">Pending</Tag>
                                        )}
                                    </div>
                                </Col>
                                <Col span={24}>
                                    <Text strong>Remarks</Text>
                                    <div>{selectedBeneficiary.remarks || "N/A"}</div>
                                </Col>
                                <Col span={24}>
                                    <Text strong>Created At</Text>
                                    <div>{getUTCToLocalDateTimeFormat(selectedBeneficiary.createdAt)}</div>
                                </Col>
                                {selectedBeneficiary.responseData && (
                                    <Col span={24}>
                                        <Text strong>Response Data</Text>
                                        <pre style={{ background: "#f5f5f5", padding: "8px", borderRadius: "4px" }}>
                                            {JSON.stringify(selectedBeneficiary.responseData, null, 2)}
                                        </pre>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
};

export default BeneficiaryManagement;